import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ShieldIcon, BookOpenIcon, CheckCircleIcon } from 'lucide-react';
export function SolutionSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="bg-[#1a2744] py-20 lg:py-28 relative overflow-hidden" data-id="element-246">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-64 h-64 bg-[#c9a227]/5 rounded-full blur-3xl" data-id="element-247" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#c9a227]/5 rounded-full blur-3xl" data-id="element-248" />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-249">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-250">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#c9a227]/20 text-[#c9a227] rounded-full text-sm font-semibold mb-6" data-id="element-251">
            <ShieldIcon className="w-4 h-4" data-id="element-252" />
            La Solución
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6" data-id="element-253">
            Presentando: <span className="text-[#c9a227]" data-id="element-254">El Escudo 1099</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-255">
            He condensado años de leyes fiscales complejas en una guía escrita
            en <strong className="text-white" data-id="element-256">español simple</strong>. Sin
            palabras técnicas, sin miedos.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-257">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-258">
            <div className="bg-[#243352] rounded-2xl p-8 border border-[#c9a227]/20" data-id="element-259">
              <div className="flex items-center gap-4 mb-6" data-id="element-260">
                <div className="w-12 h-12 bg-[#c9a227] rounded-xl flex items-center justify-center" data-id="element-261">
                  <BookOpenIcon className="w-6 h-6 text-[#1a2744]" data-id="element-262" />
                </div>
                <div data-id="element-263">
                  <h3 className="text-white font-bold text-xl" data-id="element-264">
                    El "Método de Deducción Invisible"
                  </h3>
                  <p className="text-gray-400 text-sm" data-id="element-265">
                    Sistema de 3 pasos probado
                  </p>
                </div>
              </div>

              <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-266">
                El Escudo 1099 te enseña cómo{' '}
                <strong className="text-white" data-id="element-267">legalmente recalificar</strong>{' '}
                gastos de vida como deducciones de negocio. Tu renta, tu
                gasolina y hasta tu celular pueden trabajar para{' '}
                <strong className="text-[#c9a227]" data-id="element-268">
                  bajar tu cuenta de impuestos
                </strong>
                .
              </p>

              <div className="space-y-4" data-id="element-269">
                <div className="flex items-start gap-3" data-id="element-270">
                  <CheckCircleIcon className="w-6 h-6 text-[#c9a227] flex-shrink-0 mt-0.5" data-id="element-271" />
                  <div data-id="element-272">
                    <span className="text-white font-semibold" data-id="element-273">
                      Paso 1: Identifica
                    </span>
                    <p className="text-gray-400 text-sm" data-id="element-274">
                      Aprende qué gastos califican como deducciones legales
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-275">
                  <CheckCircleIcon className="w-6 h-6 text-[#c9a227] flex-shrink-0 mt-0.5" data-id="element-276" />
                  <div data-id="element-277">
                    <span className="text-white font-semibold" data-id="element-278">
                      Paso 2: Documenta
                    </span>
                    <p className="text-gray-400 text-sm" data-id="element-279">
                      Organiza tus recibos de forma que el IRS respete
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-280">
                  <CheckCircleIcon className="w-6 h-6 text-[#c9a227] flex-shrink-0 mt-0.5" data-id="element-281" />
                  <div data-id="element-282">
                    <span className="text-white font-semibold" data-id="element-283">
                      Paso 3: Deduce
                    </span>
                    <p className="text-gray-400 text-sm" data-id="element-284">
                      Aplica las deducciones con confianza total
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="relative" data-id="element-285">
            <div className="absolute -inset-4 bg-[#c9a227]/10 rounded-3xl blur-xl" data-id="element-286" />
            <img src="https://cdn.magicpatterns.com/uploads/aDYYEtGHkUDy4q9PnMrW4y/el_escudo_1099.png" alt="El Escudo 1099 - Guía Digital" className="relative rounded-2xl shadow-2xl w-full" data-id="element-287" />
          </motion.div>
        </div>
      </div>
    </section>;
}